import { FormEvent, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { AddLiabilityInsurancePayload } from '../../Services/InsuranceLicenseService';
import { ErrorHandler } from '../../Services/UserApi';
import LiabilityInsuranceForm from './LiabilityInsuranceForm';
import { INITIAL_LIABILITY_FORM, LiabilityFormState } from './InsuranceLicenseTypes';
import {
	listFormErrors,
	MAX_EVIDENCE_FILES,
	toUnixDateString,
	validateEvidenceFile,
	validateEvidenceFiles,
	validateLiabilityForm,
} from './InsuranceLicenseUtils';

type Props = {
	addLiabilityInsurance: (payload: AddLiabilityInsurancePayload) => Promise<unknown>;
};

export default function LiabilityInsuranceAddSection(props: Props) {
	const { addLiabilityInsurance } = props;
	const history = useHistory();

	const [form, setForm] = useState(INITIAL_LIABILITY_FORM);
	const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
	const [inlineEvidenceError, setInlineEvidenceError] = useState<string | undefined>();
	const [isSaving, setIsSaving] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);

	const errors = useMemo(() => validateLiabilityForm(form), [form]);
	const errorList = useMemo(() => listFormErrors(errors), [errors]);
	const evidenceValidationError = useMemo(() => validateEvidenceFiles(evidenceFiles), [evidenceFiles]);
	const evidenceError =
		inlineEvidenceError ?? (submitAttempted || hasInteracted ? evidenceValidationError : undefined);
	const combinedErrorList = useMemo(
		() => [...errorList, ...(evidenceValidationError ? [evidenceValidationError] : [])],
		[errorList, evidenceValidationError],
	);
	const isSubmitDisabled = (hasInteracted || submitAttempted) && combinedErrorList.length > 0;

	const onFormChange = (nextForm: LiabilityFormState) => {
		setHasInteracted(true);
		setForm(nextForm);
	};

	const onAddEvidenceFiles = (fileList: FileList | null) => {
		if (!fileList) {
			return;
		}

		let firstInlineError: string | undefined;
		const nextFiles = [...evidenceFiles];
		for (const file of Array.from(fileList)) {
			if (nextFiles.length >= MAX_EVIDENCE_FILES) {
				firstInlineError = `You can upload up to ${MAX_EVIDENCE_FILES} files`;
				break;
			}
			const fileError = validateEvidenceFile(file);
			if (fileError) {
				firstInlineError = fileError;
				continue;
			}
			nextFiles.push(file);
		}

		setInlineEvidenceError(firstInlineError);
		setEvidenceFiles(nextFiles);
		setHasInteracted(true);
	};

	const onRemoveEvidenceFile = (index: number) => {
		setEvidenceFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
		setInlineEvidenceError(undefined);
		setHasInteracted(true);
	};

	const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setSubmitAttempted(true);
		if (combinedErrorList.length > 0) {
			toast.error(combinedErrorList[0] ?? 'Please check your insurance details');
			return;
		}

		setIsSaving(true);
		try {
			await addLiabilityInsurance({
				MarketClientInsuranceName: form.insuranceProvider.trim(),
				MarketClientInsurancePolicyNumber: form.policyNumber.trim(),
				MarketClientInsuranceExpiry: toUnixDateString(form.expiryDate),
				MarketClientInsuranceValidFrom: toUnixDateString(form.validFrom),
				files: evidenceFiles,
			});
			setForm(INITIAL_LIABILITY_FORM);
			setEvidenceFiles([]);
			setInlineEvidenceError(undefined);
			setHasInteracted(false);
			setSubmitAttempted(false);
			toast.success('Insurance policy added');
			history.push('/');
		} catch (error) {
			ErrorHandler.toast(error);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<section className="insurance-license-section insurance-license-section--add">
			<LiabilityInsuranceForm
				form={form}
				errors={errors}
				evidenceFiles={evidenceFiles}
				evidenceError={evidenceError}
				isSaving={isSaving}
				onSubmit={onSubmit}
				onChange={onFormChange}
				onAddEvidenceFiles={onAddEvidenceFiles}
				onRemoveEvidenceFile={onRemoveEvidenceFile}
				showAllErrors={submitAttempted || Boolean(inlineEvidenceError)}
				isSubmitDisabled={isSubmitDisabled}
			/>
		</section>
	);
}
